import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { applyProfile, applyFavicon, logoSrc } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, CheckCircle2, AlertTriangle, FileText, Loader2 } from "lucide-react";

const CHUNK = 4 * 1024 * 1024;
const day = (iso) => new Date(new Date(iso) - 1).toLocaleDateString("de-DE");

function Message({ icon: Icon, tone, title, text, testid }) {
  return (
    <div className="text-center py-6" data-testid={testid}>
      <Icon size={44} className={`mx-auto mb-3 ${tone}`} />
      <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
      <p className="text-slate-500 mt-2 text-sm">{text}</p>
    </div>
  );
}

async function sendChunks(token, file, onProgress) {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("index", i); fd.append("total", total); fd.append("filename", file.name);
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    await api.post(`/public/du/${token}/chunk`, fd);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
}

function UploadForm({ info, token, onDone }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const input = useRef(null);
  const pick = (f) => {
    setError("");
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { setError("Bitte nur PDF-Dateien hochladen."); return; }
    if (f.size > info.max_mb * 1024 * 1024) { setError(`Die Datei ist größer als ${info.max_mb} MB.`); return; }
    setFile(f);
  };
  const submit = async () => {
    setError(""); setProgress(0);
    try { await sendChunks(token, file, setProgress); onDone(file.name); } catch (e) { setError(apiError(e.response?.data?.detail)); setProgress(null); }
  };
  return (
    <div>
      <div onClick={() => input.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
        className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors" data-testid="du-dropzone">
        {file ? <div className="flex items-center justify-center gap-2 text-slate-800"><FileText size={20} /> <span className="font-medium truncate">{file.name}</span> <span className="text-slate-500 text-sm">({(file.size / 1048576).toFixed(1)} MB)</span></div>
          : <><UploadCloud size={36} className="mx-auto text-slate-400 mb-2" /><div className="font-medium text-slate-700">PDF hierher ziehen oder klicken</div><div className="text-xs text-slate-500 mt-1">nur PDF, max. {info.max_mb} MB</div></>}
        <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="du-file-input" />
      </div>
      {error && <div className="mt-3 text-sm text-red-600 flex items-center gap-1.5" data-testid="du-upload-error"><AlertTriangle size={15} /> {error}</div>}
      {progress !== null && <Progress value={progress} className="mt-4" data-testid="du-upload-progress" />}
      <p className="text-xs text-slate-500 mt-4">Wichtig: Es ist nur ein Upload möglich. Danach ist der Link gesperrt.</p>
      <Button onClick={submit} disabled={!file || progress !== null} className="w-full mt-3 bg-[#0B132B] hover:bg-[#1C2541] text-white h-11 rounded-lg" data-testid="du-upload-submit">
        {progress !== null ? <><Loader2 size={16} className="mr-2 animate-spin" /> Wird hochgeladen… {progress}%</> : "Druckunterlage hochladen"}
      </Button>
    </div>
  );
}

export default function DuUpload() {
  const { token } = useParams();
  const [brand, setBrand] = useState(null);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  useEffect(() => {
    api.get("/branding/public").then((r) => { setBrand(r.data); applyProfile(r.data.profile); applyFavicon(r.data.favicon_url, r.data.favicon_svg_url); }).catch(() => {});
    api.get(`/public/du/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [token]);
  const logo = logoSrc(brand?.logos?.sidebar);
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="brand-sidebar px-6 py-5 flex items-center gap-3" style={{ color: "var(--sidebar-text)" }}>
        {logo && <img src={logo} alt={brand?.app_name} className="h-10 object-contain" />}
        <div className="font-display font-bold text-lg">{brand?.app_name}</div>
      </header>
      <main className="flex-1 flex items-start justify-center p-4 sm:p-10">
        <div className="bg-white w-full max-w-xl rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8" data-testid="du-upload-page">
          {error && <Message icon={AlertTriangle} tone="text-red-500" title="Link ungültig" text={error} testid="du-invalid" />}
          {!error && !info && <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>}
          {info && (done || info.state === "used") && <Message icon={CheckCircle2} tone="text-emerald-600" title="Vielen Dank!" text={`Ihre Druckunterlage${done || info.filename ? ` „${done || info.filename}“` : ""} ist bei uns eingegangen. Der Link ist jetzt gesperrt. Falls Sie eine Korrektur haben, melden Sie sich bitte direkt bei uns.`} testid="du-done" />}
          {info && !done && info.state === "expired" && <Message icon={AlertTriangle} tone="text-amber-500" title="Link abgelaufen" text="Die Frist für diese Ausgabe ist vorbei. Bitte melden Sie sich direkt bei uns." testid="du-expired" />}
          {info && !done && info.state === "open" && (
            <>
              <h1 className="font-display text-2xl font-bold text-slate-900">Druckunterlage hochladen</h1>
              <div className="mt-2 text-sm text-slate-600 space-y-0.5 mb-6" data-testid="du-info">
                <div><b>{info.customer}</b> · Ausgabe {info.issue}</div>
                {info.ad_format && <div>Format: {info.ad_format}{info.ad_size ? ` (${info.ad_size})` : ""}</div>}
                {info.expires_at && <div>Upload möglich bis {day(info.expires_at)}</div>}
              </div>
              <UploadForm info={info} token={token} onDone={setDone} />
            </>
          )}
        </div>
      </main>
      <footer className="text-center text-xs text-slate-400 py-4">{brand?.footer_text}</footer>
    </div>
  );
}
